/**
 * Alaska: App.js (PWA)
 * Data: Job
 * @version 1.0
 */

document.addEventListener("alpine:init", () => {

    Alpine.data("Job", (job) => ({
        job,

        mode: "summary",

        selected_drop: null,
        active_job: null,

        pod: {
            time: "",
            date: "",
            recipient: job.dname || "Resident",
            files: [],
            previews: [],
            images: [],
            loading: false,
            modified: false,

            drivers_notes: "",

            signature_original: null, // s3 URL
            signature_url: null,
            signature_cleared: false,

            original: [],
            removed: [],
        },

        init() {
            if (this.job.is_multidrop && !Array.isArray(this.job.drops)) {
                this.job.drops = [];
            }

            this.mode = this.job.is_multidrop ? "drops" : "summary";
            this.active_job = this.job;
        },

        handle_back() {
            // POD -> Summary screens
            if (this.mode === "pod") {
                this.mode = this.job?.is_multidrop ? "drop_summary" : "summary";
                return;
            }

            // Multi-drop: drop summary -> drops list
            if (this.job?.is_multidrop && this.mode === "drop_summary") {
                this.mode = "drops";
                this.selected_drop = null;
                this.active_job = this.job;
                return;
            }

            // Multi-drop: drops list -> schedule list
            if (this.job?.is_multidrop && this.mode === "drops") {
                this.return_to_schedule();
                return;
            }

            // Single: summary -> schedule list
            if (!this.job?.is_multidrop && this.mode === "summary") {
                this.return_to_schedule();
                return;
            }

            // Fallback
            this.return_to_schedule();
        },

        // -------- Multi-drop helpers -------- //

        is_drop_complete(drop) {
            if (!drop) return false;

            // best signal: explicit boolean
            if (drop.complete === true) return true;

            // common string statuses
            const st = String(drop.status || "").toLowerCase();
            if (st === "complete") return true;

            // signature-only
            if (drop.signature && String(drop.signature).trim() !== "") return true;

            // fallback: images
            if (Array.isArray(drop.pod_images) && drop.pod_images.length > 0) return true;

            return false;
        },

        drop_label(drop, idx) {
            const total = this.job.multidrop_total || (Array.isArray(this.job.drops) ? this.job.drops.length : 0);
            return `${idx + 1} of ${total}`;
            // return `${drop.job_name || this.job.company_name || "Job"} - ${idx + 1} of ${total}`;
        },

        build_active_job_from_drop(drop) {
            // Keep booking ID as the main job.id; add drop_id for backend scoping
            return {
                ...this.job,
                ...drop,
                id: this.job.id,
                drop_id: drop.id || drop.drop_id || null,
            };
        },

        // -------- Flow starters -------- //

        start_single_pod() {
            this.selected_drop = null;
            this.active_job = this.job;
            this.mode = "pod";
            this.open_pod();
        },

        start_drop(drop) {
            this.selected_drop = drop;
            this.active_job = this.build_active_job_from_drop(drop);
            this.mode = "drop_summary";
        },

        get hide_border() {
            return this.job?.is_multidrop && this.mode === "drops";
        },

        get completed_drops_count() {
            const drops = Array.isArray(this.job?.drops) ? this.job.drops : [];
            return drops.filter(d => this.is_drop_complete(d)).length;
        },

        get total_drops_count() {
            return this.job?.multidrop_total || (Array.isArray(this.job?.drops) ? this.job.drops.length : 0);
        },

        get job_progress_state() {
            const total = this.total_drops_count;
            const done = this.completed_drops_count;

            if (!total || done <= 0) return "none";
            if (done < total) return "partial";
            return "complete";
        },

        start_drop_pod() {
            if (!this.active_job) return;
            this.mode = "pod";
            this.open_pod();
        },

        // -------- POD setup -------- //

        async open_pod() {
            const now = new Date();
            const {time, date} = this.get_date_and_time(now.toISOString());
            this.pod.time = time;
            this.pod.date = date;

            this.pod.recipient = (this.active_job?.dname || this.job?.dname || "Resident");

            const existing = Array.isArray(this.active_job?.pod_images)
                ? this.active_job.pod_images
                : (Array.isArray(this.job?.pod_images) ? this.job.pod_images : []);

            this.pod.original = [...existing];
            this.pod.removed = [];
            this.pod.images = [];
            this.pod.modified = false;

            this.pod.drivers_notes = String(this.active_job?.drivers_notes ?? this.job?.drivers_notes ?? "");

            const sig = this.active_job?.signature || this.job?.signature || null;

            this.pod.signature_original = sig;
            this.pod.signature_cleared = false;
            this.pod.signature_url = sig ? await this.fetch_signature_url(sig) : null;

            this.reset_file();
            this.fetch_pod_images();
        },

        // -------- Signature -------- //

        clear_signature() {
            this.pod.signature_cleared = true;
            this.pod.signature_original = null;
            this.pod.signature_url = null;
            this.pod.modified = true;

            // also clear canvas so user can redraw immediately
            const signature_component = this.get_signature_component();
            if (signature_component && signature_component.clear) {
                signature_component.clear();
            }
        },

        fetch_signature(path) {
            if (!path) return "";

            const filename = path.split("/").pop();
            const drop_param = this.active_job?.drop_id ? `&drop_id=${this.active_job.drop_id}` : "";

            return `/?action=pod_signature&controller=delivery&booking=${this.job.id}&filename=${encodeURIComponent(filename)}${drop_param}`;
        },

        async fetch_signature_url(path) {
            const url = this.fetch_signature(path);
            if (!url) return null;

            return await new Promise(resolve => {
                const img = new Image();
                img.onload = () => resolve(url);
                img.onerror = () => resolve(null);
                img.src = url;
            });
        },

        // -------- P.O.D Images -------- //

        async fetch_pod_images() {
            Debug.log(`Fetching pod images for job: ${this.job.id}`, "info");
            this.pod.loading = true;

            const remaining_s3_urls = this.pod.original.filter(url => !this.pod.removed.includes(url));
            const fetchable_urls = remaining_s3_urls.map(url => this.fetch_pod_image(url));

            const loaded = await Promise.all(fetchable_urls.map(url => {
                return new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => resolve(url);
                    img.onerror = () => resolve(null);
                    img.src = url;
                });
            }));

            this.pod.images = loaded.filter(Boolean);
            this.$nextTick(() => this.pod.loading = false);
        },

        fetch_pod_image(path) {
            if (!path) return "";
            const filename = path.split("/").pop();

            // for multidrop, use active_job.drop_id
            const drop_param = this.active_job?.drop_id ? `&drop_id=${this.active_job.drop_id}` : "";
            return `/?action=pod_image&controller=delivery&booking=${this.job.id}&filename=${encodeURIComponent(filename)}${drop_param}`;
        },

        remove_stored_image(index) {
            const s3_url = this.pod.original.find(url => this.fetch_pod_image(url) === this.pod.images[index]);
            if (s3_url) this.pod.removed.push(s3_url);

            this.pod.images.splice(index, 1);
            this.pod.modified = true;
        },

        open_image(image) {
            Debug.log("[JOB] Opening proof of delivery image in Modal()");
            Alpine.store("modal").open({image});
        },

        handle_file(event) {
            const selected_files = Array.from(event.target.files || []);
            selected_files.forEach(file => {
                if (!file.type.startsWith("image/")) return;
                this.pod.files.push(file);

                const reader = new FileReader();
                reader.onload = (e) => this.pod.previews.push(e.target.result);
                reader.readAsDataURL(file);
            });

            this.pod.modified = true;
        },

        remove_file(index) {
            this.pod.files.splice(index, 1);
            this.pod.previews.splice(index, 1);
            this.pod.modified = true;
        },

        reset_file() {
            this.pod.files = [];
            this.pod.previews = [];
            if (this.$refs.file_input) this.$refs.file_input.value = "";
        },

        // -------- Display helpers -------- //

        display_time() {
            return this.pod.time;
        },

        display_date() {
            return this.pod.date;
        },

        // -------- Submission -------- //

        get_signature_component() {
            if (!this.$refs.signature) return null;
            const signature_root = this.$refs.signature.querySelector('[x-data^="Signature"]');
            return signature_root ? signature_root.__signature : null;
        },

        async submit() {
            Loading.show();

            if (!this.pod.recipient.trim()) this.pod.recipient = "Resident";

            const data = new FormData();
            data.append("action", "update");
            data.append("controller", "delivery");
            data.append("delivery", this.job.id);

            // multidrop scope
            if (this.active_job?.drop_id !== undefined && this.active_job?.drop_id !== null) {
                data.append("drop_id", this.active_job.drop_id);
            }

            data.append("recipient", this.pod.recipient);
            data.append("email", Alpine.store("user").driver.email);

            data.append("drivers_notes", this.pod.drivers_notes || "");

            // retained POD images
            const retained = this.pod.original.filter(url => !this.pod.removed.includes(url));
            retained.forEach(url => data.append("retained_images[]", url));

            // uploaded POD images
            this.pod.files.forEach(file => data.append("images[]", file));

            // signature => also as images[]
            const signature_component = this.get_signature_component();
            const has_new_signature = signature_component && signature_component.has_ink && signature_component.has_ink();

            // if user clicked clear on an existing signature, send a flag
            if (this.pod.signature_cleared) {
                data.append("signature_clear", "1");
            }

            // if they drew a new signature, upload it
            if (has_new_signature) {
                const sig_file = await signature_component.to_file("png");
                data.append("signature", sig_file);
            }

            const has_any_images =
                retained.length > 0 ||
                this.pod.files.length > 0 ||
                (signature_component && signature_component.has_ink && signature_component.has_ink());

            const has_any_signature =
                has_new_signature ||
                (!!this.pod.signature_original && !this.pod.signature_cleared);

            if (!has_any_images && !has_any_signature) {
                Loading.hide();
                Alpine.store("overlay").render("error", "Please add photos or a signature.");
                return;
            }

            try {
                const res = await fetch("/", {method: "POST", body: data});
                const json = await res.json();

                Loading.hide();

                Alpine.store("user").schedule = json.schedule;
                Alpine.store("overlay").render(json.type, json.message);

                // refresh schedule list data
                const fresh = Alpine.store("user")?.schedule?.find(j => String(j.id) === String(this.job.id));
                if (fresh) {
                    this.job = fresh;
                    if (this.job.is_multidrop && !Array.isArray(this.job.drops)) this.job.drops = [];

                    // If we were working on a drop, rebind it to the refreshed drop object
                    const current_drop_id = this.active_job?.drop_id || this.selected_drop?.drop_id || this.selected_drop?.id || null;
                    if (current_drop_id && Array.isArray(this.job.drops)) {
                        const updated_drop = this.job.drops.find(d => String(d.drop_id || d.id) === String(current_drop_id));
                        if (updated_drop) {
                            this.selected_drop = updated_drop;
                            this.active_job = this.build_active_job_from_drop(updated_drop);
                        }
                    }
                }

                // now branch: multidrop advance or exit
                this.after_submit_success();

            } catch (err) {
                Loading.hide();
                Alpine.store("overlay").render("error", "Upload failed.");
                console.error(err);
            }
        },

        after_submit_success() {
            // Single job: go back to schedule
            if (!this.job.is_multidrop) {
                this.return_to_schedule();
                return;
            }

            // Multi-drop: move to next incomplete drop
            const next = this.get_next_incomplete_drop();

            if (next) {
                this.start_drop(next);
                return;
            }

            // no more drops
            this.return_to_schedule();
        },

        get_next_incomplete_drop() {
            const drops = Array.isArray(this.job?.drops) ? this.job.drops : [];
            if (!drops.length) return null;

            const current_id = this.selected_drop?.id || this.active_job?.drop_id || null;
            const start_idx = current_id ? drops.findIndex(d => String(d.id) === String(current_id)) : -1;

            const ordered = start_idx >= 0
                ? drops.slice(start_idx + 1).concat(drops.slice(0, start_idx + 1))
                : drops;

            return ordered.find(d => !this.is_drop_complete(d)) || null;
        },

        return_to_schedule() {
            // reset POD bits (optional)
            this.reset_file();
            this.pod.images = [];
            this.pod.modified = false;

            // route back
            if (Alpine.store("state")?.load) {
                Alpine.store("state").load("schedule", "index");
            } else if (Alpine.store("state")?.back) {
                Alpine.store("state").back();
            } else {
                // fallback
                window.location.href = "/?module=schedule";
            }
        },

        // -------- Date helpers -------- //

        get_date_and_time(dwhen = false) {
            if (!dwhen) return {time: "", date: ""};
            const date = new Date(dwhen);
            const time = date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            }).toLowerCase();
            const weekday = date.toLocaleDateString(undefined, {weekday: 'short'});
            const day = date.getDate();
            const suffix = this.ordinal_suffix(day);
            const month = date.toLocaleDateString(undefined, {month: 'long'});
            const year = date.getFullYear();
            return {time, date: `${weekday} ${day}${suffix} ${month}, ${year}`};
        },

        ordinal_suffix(i) {
            const j = i % 10, k = i % 100;
            if (j === 1 && k !== 11) return 'st';
            if (j === 2 && k !== 12) return 'nd';
            if (j === 3 && k !== 13) return 'rd';
            return 'th';
        },
    }));

    Alpine.data("Signature", (opts = {}) => ({
        canvas: null,
        ctx: null,

        drawing: false,
        last_x: 0,
        last_y: 0,

        dirty: false,

        // drawing style
        line_width: 3,
        line_cap: "round",
        stroke_style: "#111",

        dpr: 1,

        init() {
            this.canvas = this.$refs.canvas;

            this.$el.__signature = this;

            if (!this.canvas) {
                console.warn("[Signature] Missing x-ref='canvas'");
                return;
            }

            this.ctx = this.canvas.getContext("2d", {willReadFrequently: true});
            this.resize_canvas(true);

            // Modern
            this.canvas.addEventListener("pointerdown", (e) => this.on_down(e), {passive: false});
            this.canvas.addEventListener("pointermove", (e) => this.on_move(e), {passive: false});
            this.canvas.addEventListener("pointerup", (e) => this.on_up(e), {passive: false});
            this.canvas.addEventListener("pointercancel", (e) => this.on_up(e), {passive: false});

            // Fallback
            this.canvas.addEventListener("touchstart", (e) => this.on_touch_start(e), {passive: false});
            this.canvas.addEventListener("touchmove", (e) => this.on_touch_move(e), {passive: false});
            this.canvas.addEventListener("touchend", () => this.on_touch_end(), {passive: false});
            this.canvas.addEventListener("touchcancel", () => this.on_touch_end(), {passive: false});

            window.addEventListener("resize", () => this.resize_canvas(false));
        },

        apply_style() {
            if (!this.ctx) return;
            this.ctx.lineWidth = this.line_width;
            this.ctx.lineCap = this.line_cap;
            this.ctx.strokeStyle = this.stroke_style;
        },

        resize_canvas(preserve = true) {
            if (!this.canvas || !this.ctx) return;

            const rect = this.canvas.getBoundingClientRect();
            const new_dpr = window.devicePixelRatio || 1;

            // Store current drawing if needed
            let snapshot = null;
            if (preserve && this.canvas.width && this.canvas.height) {
                try {
                    snapshot = this.canvas.toDataURL("image/png");
                } catch (e) {
                    snapshot = null;
                }
            }

            this.dpr = new_dpr;

            // Set device pixel size
            this.canvas.width = Math.max(1, Math.floor(rect.width * this.dpr));
            this.canvas.height = Math.max(1, Math.floor(rect.height * this.dpr));

            // Map drawing ops to CSS pixels
            this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
            this.apply_style();

            // Restore drawing
            if (snapshot) {
                const img = new Image();
                img.onload = () => {
                    this.ctx.drawImage(img, 0, 0, rect.width, rect.height);
                };
                img.src = snapshot;
            }
        },

        get_pos(e) {
            const rect = this.canvas.getBoundingClientRect();
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        },

        on_down(e) {
            // only primary button/pointer
            if (e.button !== undefined && e.button !== 0) return;

            e.preventDefault();

            this.drawing = true;
            this.canvas.setPointerCapture?.(e.pointerId);

            const {x, y} = this.get_pos(e);
            this.last_x = x;
            this.last_y = y;

            // dot for tap
            this.ctx.beginPath();
            this.ctx.moveTo(x, y);
            this.ctx.lineTo(x + 0.01, y + 0.01);
            this.ctx.stroke();

            this.dirty = true;

            if (typeof opts.on_change === "function") {
                opts.on_change();
            }
        },

        on_move(e) {
            if (!this.drawing) return;

            e.preventDefault();

            const {x, y} = this.get_pos(e);

            this.ctx.beginPath();
            this.ctx.moveTo(this.last_x, this.last_y);
            this.ctx.lineTo(x, y);
            this.ctx.stroke();

            this.last_x = x;
            this.last_y = y;

            this.dirty = true;

            if (typeof opts.on_change === "function") {
                opts.on_change();
            }
        },

        on_up(e) {
            if (!this.drawing) return;

            e.preventDefault();

            this.drawing = false;

            try {
                this.canvas.releasePointerCapture?.(e.pointerId);
            } catch (_) {
            }
        },

        on_touch_start(e) {
            e.preventDefault();
            const t = e.touches[0];
            if (!t) return;
            this.drawing = true;
            const {x, y} = this.get_touch_pos(t);
            this.last_x = x;
            this.last_y = y;

            this.ctx.beginPath();
            this.ctx.moveTo(x, y);
            this.ctx.lineTo(x + 0.01, y + 0.01);
            this.ctx.stroke();

            this.dirty = true;
            if (typeof opts.on_change === "function") opts.on_change();
        },

        on_touch_move(e) {
            if (!this.drawing) return;
            e.preventDefault();
            const t = e.touches[0];
            if (!t) return;

            const {x, y} = this.get_touch_pos(t);

            this.ctx.beginPath();
            this.ctx.moveTo(this.last_x, this.last_y);
            this.ctx.lineTo(x, y);
            this.ctx.stroke();

            this.last_x = x;
            this.last_y = y;

            this.dirty = true;
            if (typeof opts.on_change === "function") opts.on_change();
        },

        on_touch_end() {
            this.drawing = false;
        },

        get_touch_pos(touch) {
            const rect = this.canvas.getBoundingClientRect();
            return {x: touch.clientX - rect.left, y: touch.clientY - rect.top};
        },

        clear() {
            if (!this.canvas || !this.ctx) return;

            // clear using *device pixels*
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

            // restore DPR transform for drawing
            this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
            this.apply_style();

            this.dirty = false;

            if (typeof opts.on_clear === "function") {
                opts.on_clear();
            }
        },

        // Fast check
        has_ink() {
            return this.dirty;
        },

        // Strict check: scan alpha channel
        has_ink_strict() {
            const w = this.canvas.width;
            const h = this.canvas.height;
            const data = this.ctx.getImageData(0, 0, w, h).data;

            for (let i = 3; i < data.length; i += 4) {
                if (data[i] !== 0) return true;
            }
            return false;
        },

        canvas_to_blob(mime) {
            return new Promise((resolve) => {
                const rect = this.canvas.getBoundingClientRect();

                // jpeg: bake in white background
                if (mime === "image/jpeg") {
                    const tmp = document.createElement("canvas");
                    tmp.width = Math.max(1, Math.floor(rect.width * this.dpr));
                    tmp.height = Math.max(1, Math.floor(rect.height * this.dpr));

                    const tctx = tmp.getContext("2d");
                    tctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

                    tctx.fillStyle = "#fff";
                    tctx.fillRect(0, 0, rect.width, rect.height);

                    // Draw current canvas into temp
                    tctx.drawImage(this.canvas, 0, 0, rect.width, rect.height);

                    tmp.toBlob((b) => resolve(b), mime, 0.9);
                    return;
                }

                this.canvas.toBlob((b) => resolve(b), mime);
            });
        },

        async to_file(type = "png") {
            const mime = (type === "jpg" || type === "jpeg") ? "image/jpeg" : "image/png";
            const blob = await this.canvas_to_blob(mime);

            if (!blob) {
                throw new Error("[Signature] Failed to create blob from canvas");
            }

            const ext = mime === "image/jpeg" ? "jpg" : "png";
            const filename = `signature-${Date.now()}.${ext}`;

            return new File([blob], filename, {type: mime});
        },

    }));

});
